import { useEffect, useState } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { useQueryClient } from '@tanstack/react-query'
import type { CreateCompanyManagementUserModel, UpdateCompanyManagementUserModel } from '@/api/model'
import {
  usePostApiCompaniesCurrentCompanymanagementusers,
  usePatchApiCompaniesCurrentCompanymanagementusersCompanyManagementUserId,
  useDeleteApiCompaniesCurrentCompanymanagementusersCompanyManagementUserId,
  getGetApiCompaniesCurrentCompanymanagementusersQueryKey,
} from '@/api/generated/company-management-user/company-management-user'
import { useGetApiMe } from '@/api/generated/user/user'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/shadcn/dialog'
import { Button } from '@/components/shadcn/button'
import { Input } from '@/components/shadcn/input'
import { Label } from '@/components/shadcn/label'
import { notify } from '@/lib/notify'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import type { CompanyManagementUser } from '../permissions.types'

type FormValues = {
  firstName: string
  lastName: string
  email: string
}

const defaultValues: FormValues = { firstName: '', lastName: '', email: '' }

interface Props {
  isOpen: boolean
  onOpenChange: (open: boolean) => void
  user?: CompanyManagementUser | null
  // CompanyManagementUsers.Delete — no deletion without this permission.
  canDelete?: boolean
  // After deletion: let the page clean up a possibly selected row.
  onDeleted?: (userId: string) => void
}

export function UserDialog({ isOpen, onOpenChange, user, canDelete = false, onDeleted }: Props) {
  const { t } = useTranslation(['permissions', 'common'])
  const isEdit = !!user
  const queryClient = useQueryClient()
  const { register, handleSubmit, reset, watch } = useForm<FormValues>({ defaultValues })

  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false)

  // You may not delete yourself (others are fine).
  const { data: meData } = useGetApiMe()
  const isSelf = isEdit && user!.id === meData?.data?.id

  useEffect(() => {
    if (isOpen) {
      reset(isEdit ? {
        firstName: user.firstName ?? '',
        lastName: user.lastName ?? '',
        email: user.email ?? '',
      } : defaultValues)
    }
  }, [isOpen, user])

  const invalidate = () => queryClient.invalidateQueries({
    queryKey: getGetApiCompaniesCurrentCompanymanagementusersQueryKey(),
  })

  const { mutate: createUser, isPending: isCreating } = usePostApiCompaniesCurrentCompanymanagementusers({
    mutation: {
      onSuccess: () => { notify.success(t('userDialog.toasts.created')); onOpenChange(false); invalidate() },
      onError: (error) => notify.apiError(error, t('userDialog.toasts.createError')),
    },
  })

  const { mutate: updateUser, isPending: isUpdating } = usePatchApiCompaniesCurrentCompanymanagementusersCompanyManagementUserId({
    mutation: {
      onSuccess: () => { notify.success(t('userDialog.toasts.updated')); onOpenChange(false); invalidate() },
      onError: (error) => notify.apiError(error, t('userDialog.toasts.updateError')),
    },
  })

  const { mutate: deleteUser, isPending: isDeleting } = useDeleteApiCompaniesCurrentCompanymanagementusersCompanyManagementUserId({
    mutation: {
      onSuccess: () => {
        notify.success(t('userDialog.toasts.deleted'))
        setConfirmDeleteOpen(false)
        onOpenChange(false)
        if (user) onDeleted?.(user.id)
        invalidate()
      },
      onError: (error) => notify.apiError(error, t('userDialog.toasts.deleteError')),
    },
  })

  const isPending = isCreating || isUpdating || isDeleting

  const onValid = (values: FormValues) => {
    if (isEdit) {
      const payload: UpdateCompanyManagementUserModel = {
        firstName: values.firstName || null,
        lastName: values.lastName || null,
      }
      updateUser({ companyManagementUserId: user!.id, data: payload })
    } else {
      const payload: CreateCompanyManagementUserModel = {
        firstName: values.firstName || null,
        lastName: values.lastName || null,
        email: values.email || null,
      }
      createUser({ data: payload })
    }
  }

  const handleDelete = () => {
    if (!user || isSelf) return
    setConfirmDeleteOpen(true)
  }

  const values = watch()
  const canSubmit = isEdit
    ? !!values.firstName && !!values.lastName
    : !!values.firstName && !!values.lastName && !!values.email

  return (
    <>
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{isEdit ? t('userDialog.titleEdit') : t('userDialog.titleCreate')}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? t('userDialog.descriptionEdit')
              : t('userDialog.descriptionCreate')}
          </DialogDescription>
        </DialogHeader>

        <form id="user-form" onSubmit={handleSubmit(onValid)} className="flex flex-col gap-4 py-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="firstName">{t('userDialog.firstName')}</Label>
            <Input id="firstName" {...register('firstName')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="lastName">{t('userDialog.lastName')}</Label>
            <Input id="lastName" {...register('lastName')} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor="email">{t('userDialog.email')}</Label>
            {/* Email editable only on create — fixed in edit mode. */}
            <Input id="email" type="email" disabled={isEdit} {...register('email')} />
          </div>
        </form>

        <DialogFooter className={isEdit && canDelete ? 'flex-row justify-between sm:justify-between' : undefined}>
          {isEdit && canDelete && (
            <Button
              type="button"
              variant="destructive"
              onClick={handleDelete}
              disabled={isPending || isSelf}
              title={isSelf ? t('userDialog.deleteSelfTooltip') : undefined}
            >
              {t('buttons.delete', { ns: 'common' })}
            </Button>
          )}
          <div className="flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>{t('buttons.cancel', { ns: 'common' })}</Button>
            <Button type="submit" form="user-form" disabled={!canSubmit || isPending}>
              {isEdit ? t('buttons.save', { ns: 'common' }) : t('userDialog.create')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    <ConfirmDialog
      open={confirmDeleteOpen}
      title={t('userDialog.confirmDelete.title')}
      description={t('userDialog.confirmDelete.description', { name: [user?.firstName, user?.lastName].filter(Boolean).join(' ') })}
      confirmLabel={t('buttons.delete', { ns: 'common' })}
      confirmButtonVariant="destructive"
      isLoading={isDeleting}
      onConfirm={() => user && deleteUser({ companyManagementUserId: user.id })}
      onClose={() => setConfirmDeleteOpen(false)}
    />
    </>
  )
}
